import { useState } from 'react';
import { GAME_MODES, MODE_LABELS, type GameMode } from '../../shared/game/knobs';
import type { PlayerRule } from '../../shared/game/rule';
import { FAMILY_DISPLAY_NAMES, buildSystem, countTiles, type TileFamilyId } from '../../shared/tiles';
import { ONLINE_URL, SOLO_ONLY, type Mode } from './App';
import { SOLO_LEVELS, SOLO_MAX_PER_KIND, type SoloOptions } from './local';
import { BOT_INFO, BOT_KINDS } from '../../shared/game/bots';
import { RuleEditor } from './RuleEditor';
import type { Store } from './store';
import { FeedbackButton } from './FeedbackButton';
import { SettingsButton } from './SettingsButton';
import { Leaderboard } from './Leaderboard';

export interface LobbyProps {
  readonly store: Store;
  readonly mode: Mode;
  readonly solo: SoloOptions;
  readonly gameMode: GameMode;
  /** The online field size picked (null: the server's default). */
  readonly level: number | null;
  onLevel(level: number): void;
  readonly rule: PlayerRule;
  readonly name: string;
  /** Already in the arena: this is a "new rule" restart, not a first entry. */
  readonly inArena: boolean;
  /** Why the player is back here, if not by choice. */
  readonly notice?: string;
  /** Online and can't reach the server after a few tries. */
  readonly struggling: boolean;
  /** Give up on the online arena and switch to solo. */
  onGiveUp(): void;
  /** The room a `?room=` link names, if any (online only). */
  readonly linkRoom: string | null;
  onMode(mode: Mode): void;
  onSolo(opts: SoloOptions): void;
  onGameMode(mode: GameMode): void;
  onRule(rule: PlayerRule): void;
  onName(name: string): void;
  onEnter(): void;
  /** Swap captured pattern `index` for this rule (its lines and their points go). */
  onSwap(index: number): void;
  onCancel(): void;
  /** Leave the arena altogether, back to the full main screen. */
  onLeave(): void;
}

/** Where a `?room=` link leads: into that room, or opening it. */
function LinkRoomNote({ room, store, gameMode }: { room: string; store: Store; gameMode: GameMode }): JSX.Element {
  const there = store.hello?.rooms.find((r) => r.id === room);
  // The same page without the link: matchmaking again.
  const params = new URLSearchParams(location.search);
  params.delete('room');
  const q = params.toString();
  const publicHref = `${location.pathname}${q ? `?${q}` : ''}`;
  return (
    <p className="lobby-room">
      {there ? (
        <>
          Joining room <b>{room}</b> · {MODE_LABELS[there.mode]}
          {there.level !== undefined ? ` · level ${there.level}` : ''} · {there.players} playing.
        </>
      ) : (
        <>
          Opening room <b>{room}</b> · {MODE_LABELS[gameMode]}. Anyone with this link joins you there.
        </>
      )}{' '}
      <a href={publicHref}>Play in a public room instead</a>
    </p>
  );
}

function tileCount(family: TileFamilyId, level: number): number {
  return countTiles(buildSystem(family, level)['Delta']);
}

export function Lobby(props: LobbyProps): JSX.Element {
  const { store, mode, solo, gameMode, level, onLevel, rule, name, inArena, notice, struggling, onGiveUp, linkRoom, onMode, onSolo, onGameMode, onRule, onName, onEnter, onSwap, onCancel, onLeave } =
    props;
  const [touched, setTouched] = useState(false);
  const [drafting, setDrafting] = useState<readonly string[]>([]);
  const [tab, setTab] = useState<'play' | 'leaderboard'>('play');
  const hello = store.hello;
  // The online size: the one picked if this server offers it, else its default.
  const levels = hello?.levels ?? [];
  const online = levels.find((l) => l.level === level) ?? levels.find((l) => l.level === hello?.field.level);
  /** Humans in rooms of a level (rooms from older servers are the default's). */
  const playingAt = (lv: number): number =>
    (hello?.rooms ?? []).filter((r) => (r.level ?? hello?.field.level) === lv && r.mode === gameMode).reduce((n, r) => n + r.players, 0);
  const ready = rule.subset.length > 0 && name.trim().length > 0 && drafting.length === 0;
  // Before the server hands out a player colour, the chords wear Spectre's accent.
  const color = store.me?.color ?? '#6ea8fe';
  // Captured slots (each worth a head) can take this rule instead of a restart.
  const slots = inArena && store.me ? store.me.patterns.map((q, i) => ({ q, i })).filter(({ i }) => i > 0) : [];
  // The leaderboard is the server's: online, from the lobby.
  const tabs = !SOLO_ONLY && mode === 'online' && !inArena && !!hello;
  const board = tabs && tab === 'leaderboard';

  return (
    <div className="lobby">
      <header className="lobby-head">
        <div>
          <h1>Spectacle</h1>
          <p className="muted">
            A massively multiplayer strand-drawing game on {hello ? FAMILY_DISPLAY_NAMES[hello.field.family].toLowerCase() : 'tiles'}.
            {hello && mode === 'online' ? ` ${(online?.tiles ?? hello.tiles).toLocaleString()} tiles, ${hello.players} playing.` : ''}
          </p>
        </div>
        <span className="head-buttons">
          <FeedbackButton game={() => ({ mode, gameMode, room: store.room, field: hello?.field, rule })} />
          <SettingsButton />
          <a
            className="btn settings-btn github-link"
            href="https://github.com/bohemian-miser/Spectacle"
            target="_blank"
            rel="noreferrer"
            aria-label="Spectacle on GitHub"
            title="Spectacle on GitHub"
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="currentColor">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
            </svg>
          </a>
        </span>
      </header>

      {tabs && (
        <nav className="lobby-tabs" role="tablist" aria-label="Lobby">
          {(['play', 'leaderboard'] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`lobby-tab${tab === t ? ' is-on' : ''}`} onClick={() => setTab(t)}>
              {t === 'play' ? 'Play' : 'Leaderboard'}
            </button>
          ))}
        </nav>
      )}

      {!inArena && !board && (
        <section className="panel">
          <h2>Where</h2>
          {notice && (
            <p className="lobby-notice">
              {notice}
              {mode === 'online' && (
                <>
                  {' '}
                  <button type="button" className="btn-link" onClick={onGiveUp}>
                    Play bots instead
                  </button>
                </>
              )}
            </p>
          )}
          {struggling && !notice && (
            // Here, not beside "Connecting…": that line only shows before the first
            // hello, and a server that drops later leaves hello (and the rule editor) up.
            <p className="lobby-notice">
              Can't reach the arena right now.{' '}
              <button type="button" className="btn-link" onClick={onGiveUp}>
                Play bots instead
              </button>
              , no server needed.
            </p>
          )}
          {linkRoom && <LinkRoomNote room={linkRoom} store={store} gameMode={gameMode} />}
          {!SOLO_ONLY && (
            <div className="mode-row">
              <button type="button" className={`btn${mode === 'online' ? ' is-on' : ''}`} onClick={() => onMode('online')}>
                Online arena
              </button>
              <button type="button" className={`btn${mode === 'solo' ? ' is-on' : ''}`} onClick={() => onMode('solo')}>
                Solo, in this tab
              </button>
            </div>
          )}
          <div className="mode-row" role="radiogroup" aria-label="Game mode">
            {GAME_MODES.map((m) => {
              const rooms = hello && mode === 'online' ? hello.rooms.filter((r) => r.mode === m) : [];
              const playing = rooms.reduce((n, r) => n + r.players, 0);
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={gameMode === m}
                  className={`btn${gameMode === m ? ' is-on' : ''}`}
                  onClick={() => onGameMode(m)}
                >
                  {MODE_LABELS[m]}
                  {rooms.length > 0 && <span className="muted"> · {playing} playing</span>}
                </button>
              );
            })}
          </div>
          {mode === 'online' && levels.length > 1 && !linkRoom && (
            <div className="solo-row">
              <label>
                Size
                <select value={online?.level} onChange={(e) => onLevel(Number(e.target.value))}>
                  {levels.map((l) => {
                    const n = playingAt(l.level);
                    return (
                      <option key={l.level} value={l.level}>
                        level {l.level} · {l.tiles.toLocaleString()} tiles{n > 0 ? ` · ${n} playing` : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          )}
          {mode === 'solo' && (
            <div className="solo-row">
              <label>
                Size
                <select value={solo.level} onChange={(e) => onSolo({ ...solo, level: Number(e.target.value) })}>
                  {SOLO_LEVELS.map((lv) => (
                    <option key={lv} value={lv}>
                      level {lv} · {tileCount(solo.family, lv).toLocaleString()} tiles
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="bot-picks">
                <legend>Bots</legend>
                {BOT_KINDS.map((k) => (
                  <label key={k} className="bot-pick">
                    <input
                      type="number"
                      min={0}
                      max={SOLO_MAX_PER_KIND}
                      value={solo.bots[k] ?? 0}
                      aria-label={`${BOT_INFO[k].label} bots`}
                      onChange={(e) => onSolo({ ...solo, bots: { ...solo.bots, [k]: Math.max(0, Math.min(SOLO_MAX_PER_KIND, Math.floor(Number(e.target.value)) || 0)) } })}
                    />
                    <span className="bot-name">{BOT_INFO[k].label}</span>
                    <span className="bot-blurb">{BOT_INFO[k].blurb}</span>
                  </label>
                ))}
              </fieldset>
              <span className="muted">
                Everything runs in your browser; nothing is shared.
                {SOLO_ONLY && ONLINE_URL && (
                  <>
                    {' '}
                    <a href={ONLINE_URL}>Play online with others →</a>
                  </>
                )}
              </span>
            </div>
          )}
        </section>
      )}

      <section className="panel" hidden={board}>
        <input
          className="input"
          value={name}
          maxLength={store.knobs?.maxNameLength ?? 16}
          placeholder="player name"
          disabled={inArena}
          onChange={(e) => {
            setTouched(true);
            onName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) onEnter();
          }}
          autoFocus={!inArena}
        />
        {touched && name.trim().length === 0 && <span className="tag tag-bad">say who you are</span>}
      </section>

      {board && hello && (
        <Leaderboard
          family={hello.field.family}
          mode={gameMode}
          levels={levels.map((l) => l.level)}
          level={online?.level ?? hello.field.level}
          onLevel={onLevel}
          onUse={(r) => {
            onRule(r);
            setTab('play');
          }}
        />
      )}

      <section className="panel" hidden={board}>
        <div className="lobby-intro">
          <p>
            These fractals are called “Spectacles” based off the{' '}
            <a href="https://bohemian-miser.github.io/Spectre/" target="_blank" rel="noreferrer">
              Spectre tiling circuits
            </a>{' '}
            (see the link if you're interested in the maths).
          </p>
          <p>
            The arena is a special layout of the hexagons below. 1s join up with 1s, 2s with 2s, etc... To make sure a
            line never hits a dead end, either all the 1s need to have a line connected, or none of them. If any hexagon
            has an odd number of active edges, then that's a dead end 'tail'.
          </p>
          <p>Once you've created a pattern you like, you'll then play using that pattern.</p>
          <p>If any of that was confusing just give it a go, you'll work it out</p>
          <p className="lobby-sig">- bo miser</p>
        </div>
        {hello ? (
          <RuleEditor family={hello.field.family} rule={rule} color={color} onChange={onRule} onDrafting={setDrafting} />
        ) : mode === 'online' ? (
          <p className="muted">Connecting to the arena…</p>
        ) : (
          <p className="muted">Building the field…</p>
        )}
      </section>

      <footer className="lobby-foot" hidden={board}>
        {inArena && (
          <>
            <button type="button" className="btn" onClick={onCancel}>
              Back
            </button>
            <button type="button" className="btn" onClick={onLeave} title="Leave the arena: pick a game mode, online or solo">
              Leave arena
            </button>
          </>
        )}
        <button type="button" className="btn btn-accent btn-big" disabled={!ready || !hello} onClick={onEnter}>
          {inArena ? 'Restart with this rule' : SOLO_ONLY ? 'Play' : mode === 'solo' ? 'Play solo' : 'Enter the arena'}
        </button>
        {slots.map(({ q, i }) => (
          <button
            key={i}
            type="button"
            className="btn"
            disabled={!ready || !hello}
            title="Keeps the slot and its head; every line drawn with that pattern goes, with its points"
            onClick={() => onSwap(i)}
          >
            <span className="swatch" style={{ background: q.color }} /> Swap for {q.from === undefined ? `pattern ${i + 1}` : `${q.fromName || 'someone'}'s pattern`}
          </button>
        ))}
        {drafting.length > 0 && <span className="tag tag-bad">finish pairing {drafting.join(', ')} first</span>}
        {inArena && (
          <span className="muted">
            {store.knobs?.regrowOnRule
              ? store.knobs.scoreTiles
                ? 'A new rule redraws as many of your tiles as it can: its circuits through them, longest first, grow back from there'
                : 'A new rule spends your score on its circuits through the tiles you hold, longest first; they grow back from there'
              : `Restarting wipes your lines${store.knobs?.resetScoreOnRule ? ' and score' : ''}`}
            {slots.length > 0 ? `; swapping a pattern wipes only its lines${store.knobs?.scoreTiles ? '' : ', and the points they earned'}` : ''}.
          </span>
        )}
      </footer>
    </div>
  );
}
